/**
 * @fileoverview Refresh de branding em background — pega mudanças
 * que admin do parceiro fez na cor/logo/nome do provider sem exigir
 * logout do user.
 *
 * **Por que existe**: branding é persistido em localStorage
 * (`iptv:session.branding`) quando user digita o código. Sem refresh,
 * mudanças do admin nunca chegam ao cliente até user limpar storage.
 * Bug reportado por Fernando 2026-05-08 (PR #37).
 *
 * **Quando refresca:**
 * - **Boot**: 1× ao montar, se já tem `mode='provider'` + `providerCode`
 *   na sessão persistida. Pega mudanças entre sessões.
 * - **Visibility wake-up** (futuro): ao voltar de standby após >6h.
 *
 * **Falha silenciosa**: se backend offline ou /branding 404, mantém
 * branding cached. Degradação graceful (cliente continua funcionando
 * com cor antiga até backend voltar).
 *
 * **Custo**: 1 GET pequeno (<2KB) por boot quando há provider ativo.
 * Zero se modo direct ou pré-provider.
 */

import { ProviderBrandingSchema } from '@app-iptv/shared'
import { useEffect, useRef } from 'react'

import { api } from '../api'
import { useSessionStore } from '../store'

export function useBrandingRefresh(): void {
  const mode = useSessionStore((s) => s.mode)
  const providerCode = useSessionStore((s) => s.providerCode)
  const updateBranding = useSessionStore((s) => s.updateBranding)
  // Guarda POR providerCode (não 1× por boot). Logout + login em
  // outro provider mid-session re-dispara refresh — sem isso, branding
  // novo do parceiro novo nunca era refrescado nessa sessão.
  const lastFetchedCodeRef = useRef<string | null>(null)

  useEffect(() => {
    // Só pra modo provider — direct mode não tem branding remoto.
    if (mode !== 'provider' || !providerCode) return
    if (lastFetchedCodeRef.current === providerCode) return
    lastFetchedCodeRef.current = providerCode

    void api
      .getBranding(providerCode)
      .then((fresh) => {
        // Defesa em profundidade: valida via Zod antes de gravar.
        // Backend pode regredir e mandar shape errado; sem parse, gravava
        // no storage e BrandProvider tentava setProperty com non-string.
        const parsed = ProviderBrandingSchema.safeParse(fresh)
        if (!parsed.success) return
        updateBranding(parsed.data)
      })
      .catch(() => {
        // Silencioso. Se backend offline, branding cached continua válido.
      })
  }, [mode, providerCode, updateBranding])
}
